// src/pages/VehicleV2/BatteryCardMaintenanceReport_AllPrint.jsx
// Uses the SAME API as BatteryCardMaintenanceReport listing.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import companyLogo from "../../assets/infraLogo.jpg";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceReport";

const toStr = (v) => String(v ?? "").trim();

const isSelectLike = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select" || s === "all";
};

const pretty = (v) => (isSelectLike(v) ? "ALL" : toStr(v));

const display = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "—" : String(v);

const pick = (row, keys) => {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null && String(row[k]).trim() !== "") {
      return row[k];
    }
  }
  const lower = {};
  Object.keys(row || {}).forEach((k) => {
    lower[k.toLowerCase()] = row[k];
  });
  for (const k of keys) {
    const v = lower[String(k).toLowerCase()];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-GB");
  return String(v);
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function mapFiltersToParams(filters) {
  const loginUserId =
    toStr(filters.loginUserId) ||
    toStr(typeof window !== "undefined" ? sessionStorage.getItem("loginUserId") : "") ||
    toStr(typeof window !== "undefined" ? localStorage.getItem("userId") : "") ||
    toStr(typeof window !== "undefined" ? localStorage.getItem("loginuserid") : "");

  const branchType =
    toStr(filters.branchType) ||
    toStr(typeof window !== "undefined" ? localStorage.getItem("branchType") : "") ||
    toStr(typeof window !== "undefined" ? localStorage.getItem("branchtype") : "") ||
    "HO";

  return {
    branchType,
    selectedBranchId: toStr(filters.selectedBranchId || filters.branchId || "Select") || "Select",
    loginUserId,
    fromDate: toStr(filters.fromDate),
    toDate: toStr(filters.toDate),
    batteryCode: toStr(filters.batteryCode || "Select") || "Select",
    batteryType: toStr(filters.batteryType || "Select") || "Select",
    batterySerialNo: toStr(filters.batterySerialNo || "Select") || "Select",
    store: toStr(filters.store || "Select") || "Select",
    vehicleCode: toStr(filters.vehicleCode),
    vehicleName: toStr(filters.vehicleName),
    vehicleRegNo: toStr(filters.vehicleRegNo),
    batteryStatus: toStr(filters.batteryStatus || "Select") || "Select",
    vehicleMainTypeIndex:
      toStr(filters.vehicleMainTypeIndex || filters.vehicleMainType || "Select") || "Select",
    vehicleStatus: toStr(filters.vehicleStatus || "Select") || "Select",
  };
}

const COLUMNS = [
  { key: "sno", label: "S.No", w: "28px" },
  { key: "vehicleMainType", label: "Vehicle Main Type", w: "78px" },
  { key: "vehicleCode", label: "Vehicle Code", w: "56px" },
  { key: "vehicleName", label: "Vehicle Name", w: "70px" },
  { key: "vehicleRegNo", label: "Vehicle Regno", w: "70px" },
  { key: "batteryCode", label: "Battery Code", w: "72px" },
  { key: "batterySerialNo", label: "Battery Serial No", w: "70px" },
  { key: "batteryType", label: "Battery Type", w: "56px" },
  { key: "batteryMake", label: "Battery Make", w: "56px" },
  { key: "issuedBranch", label: "Issued Branch", w: "60px" },
  { key: "currentBranch", label: "Current Branch", w: "60px" },
  { key: "purchasedAmt", label: "Purchased Amt", w: "56px", align: "right" },
  { key: "issuedDate", label: "Issued Date", w: "56px" },
  { key: "expectedLife", label: "Expected Life", w: "52px" },
  { key: "supplier", label: "Supplier", w: "70px" },
  { key: "purpose", label: "Purpose of Purchase", w: "72px" },
];

export default function BatteryCardReport_AllPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const filters = useMemo(() => location?.state?.filters || {}, [location]);
  const params = useMemo(() => mapFiltersToParams(filters), [filters]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const finYear =
    typeof window !== "undefined" ? localStorage.getItem("finYear") || "" : "";

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const headers = {
          "x-finyearid": localStorage.getItem("finYearId") || "",
          "x-finyear": localStorage.getItem("finYear") || "",
          "x-branchid":
            localStorage.getItem("branchId") ||
            localStorage.getItem("Branchid") ||
            localStorage.getItem("branchid") ||
            "",
          "x-branch": params.branchType || "HO",
          "x-branchtype": params.branchType || "HO",
          "x-userid": params.loginUserId || "",
        };

        const res = await axios.get(`${API_BASE}/list`, {
          params,
          headers,
        });

        if (!res?.data?.success) {
          throw new Error(res?.data?.message || "Failed to load print data");
        }

        const apiRows = res?.data?.rows || [];

        const normalized = apiRows.map((r, i) => ({
          sno: i + 1,
          vehicleMainType: pick(r, ["VehicleType", "fld_vehicletypename", "vehicleMainType"]),
          vehicleCode: pick(r, ["fld_vehicle_code", "vehicleCode", "VehicleCode"]),
          vehicleName: pick(r, ["fld_Vehiclename", "fld_vehicle_name", "vehicleName"]),
          vehicleRegNo: pick(r, ["fld_vehicle_regno", "vehicleRegNo"]),
          batteryCode: pick(r, ["fld_batterycode", "batteryCode"]),
          batterySerialNo: pick(r, ["fld_battery_serialno", "batterySerialNo"]),
          batteryType: pick(r, ["fld_batterytype", "batteryType"]),
          batteryMake: pick(r, ["fld_batterymake", "batteryMake"]),
          issuedBranch: pick(r, ["fld_BranchName", "fld_issuedbranch", "IssuedBranch"]),
          currentBranch: pick(r, ["fld_currentbranch", "CurrentBranch", "fld_BranchName"]),
          purchasedAmt: fmtMoney(pick(r, ["fld_purchaseamt", "purchasedAmt"])),
          issuedDate: fmtDate(pick(r, ["fld_dateofreceipt", "fld_Fitteddate", "issuedDate"])),
          expectedLife: pick(r, ["fld_actuallife", "fld_expectedlife", "expectedLife"]),
          supplier: pick(r, ["fld_suppliername", "supplier"]),
          purpose: pick(r, ["fld_purpose", "purpose"]),
        }));

        if (alive) setRows(normalized);
      } catch (e) {
        if (alive) {
          setErr(e?.response?.data?.message || e.message || "Failed to load print data");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const currentDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <div className="report-page w-full bg-white">
      <style>{`
        .report-page {
          color: #1c2430;
          font-family: "Segoe UI", Calibri, Arial, sans-serif;
        }
        .report-sheet {
          max-width: 100%;
          overflow: visible;
        }
        .letterhead { border: 1.5px solid #163a5f; }
        .letterhead-topbar {
          height: 6px;
          background: linear-gradient(90deg, #0f2a45 0%, #163a5f 55%, #e8a317 100%);
        }
        .letterhead-body {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 12px;
        }
        .logo-wrap img { max-width: 72px; max-height: 60px; object-fit: contain; }
        .company-block { flex: 1; text-align: center; }
        .brand-name { margin: 0; font-size: 18px; font-weight: 800; color: #163a5f; letter-spacing: 1px; }
        .legal-name { margin: 2px 0 0; font-size: 11px; font-weight: 600; }
        .addr-line { margin: 3px 0 0; font-size: 10px; color: #4b5563; }
        .letterhead-bottombar { height: 3px; background: #e8a317; }
        .doc-title { margin: 8px 0 2px; text-align: center; font-size: 15px; font-weight: 800; color: #163a5f; }
        .title-rule { width: 80px; height: 3px; background: #e8a317; margin: 4px auto 8px; }

        .rpt-wrap {
          width: 100%;
          overflow: visible;
        }
        .rpt-table {
          width: 100%;
          table-layout: fixed;
          border-collapse: collapse;
        }
        .rpt-table th {
          background: #163a5f;
          color: #fff;
          font-size: 7.2px;
          font-weight: 700;
          padding: 4px 2px;
          border: 1px solid #0f2a45;
          text-align: center;
          line-height: 1.15;
          white-space: normal;
          word-break: break-word;
        }
        .rpt-table td {
          border: 1px solid #d0d7e0;
          padding: 3px 2px;
          font-size: 7.2px;
          text-align: center;
          vertical-align: top;
          white-space: normal;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .rpt-table td.left { text-align: left; }
        .rpt-table td.right { text-align: right; }
        .rpt-table tbody tr:nth-child(even) { background: #f7f9fc; }

        @media print {
          @page {
            size: A4 landscape;
            margin: 6mm;
          }
          html, body {
            background: #fff !important;
            width: 100% !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { display: none !important; }
          .report-page, .report-sheet, .rpt-wrap {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          .rpt-table {
            width: 100% !important;
            table-layout: fixed !important;
          }
          .rpt-table thead { display: table-header-group; }
          .rpt-table tbody tr { break-inside: avoid; }
          .rpt-table th {
            background: #163a5f !important;
            color: #fff !important;
          }
        }
      `}</style>

      <div className="report-sheet p-3 print:p-0">
        <div className="no-print flex items-center justify-between mb-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
            type="button"
          >
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-[#163a5f] text-white font-semibold rounded shadow"
            type="button"
          >
            Print Report
          </button>
        </div>

        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading report...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        <header className="letterhead">
          <div className="letterhead-topbar" />
          <div className="letterhead-body">
            <div className="logo-wrap">
              <img
                src={companyLogo}
                alt="INFRACONSOFT"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div className="company-block">
              <h1 className="brand-name">INFRACONSOFT</h1>
              <p className="legal-name">Nissi Infotech | Nissi Civil Construction</p>
              <p className="addr-line">
                3/42-12, Nagappa Colony, Kavundampalayam — 641030, Tamil Nadu, India
                <br />
                Phone: +91 90037 44644
              </p>
            </div>
            <div style={{ textAlign: "right", fontSize: 11 }}>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#5b6b82" }}>REPORT DATE</div>
              <div style={{ fontWeight: 700, color: "#163a5f" }}>{currentDate}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#5b6b82", marginTop: 6 }}>FIN YEAR</div>
              <div style={{ fontWeight: 700, color: "#163a5f" }}>{finYear || "—"}</div>
            </div>
          </div>
          <div className="letterhead-bottombar" />
        </header>

        <h2 className="doc-title">BATTERY CARD MAINTENANCE REPORT</h2>
        <div className="title-rule" />

        <div style={{ fontSize: 10, marginBottom: 8 }}>
          Branch: <b>{pretty(params.selectedBranchId)}</b>
          {" · "}
          User: <b>{pretty(params.loginUserId)}</b>
          {" · "}
          Records: <b>{loading ? "…" : rows.length}</b>
        </div>

        <div className="rpt-wrap">
          <table className="rpt-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.key} style={{ width: c.w }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length}>No records found.</td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.sno}>
                    {COLUMNS.map((c) => (
                      <td
                        key={c.key}
                        className={c.align === "right" ? "right" : c.key === "sno" ? "" : "left"}
                      >
                        {display(r[c.key])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <p style={{ marginTop: 10, fontSize: 9, color: "#64748b", textAlign: "center" }}>
          Report generated by ERP-Software · www.infraconsoft.com · owned by www.nissiinfotech.com
        </p>
      </div>
    </div>
  );
}